import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { Col, Row, Badge } from "antd";
import { MdEngineering } from "react-icons/md";
import { FcComboChart } from "react-icons/fc";
import { IoSettingsOutline } from "react-icons/io5";
import { SiZigbee2Mqtt } from "react-icons/si";
import { usePermissions } from "../../context/PermissionContext";
import { getModulesFromMenu } from "../../utils/menuConfig";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getUserDetails = () => {
  try {
    const raw = Cookies.get("user_details");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const formatRole = (role = "") =>
  role
    .replace(/^ROLE_/, "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
};

const ACTIONS = [
  { key: "read",   label: "View",   color: "#3b82f6" },
  { key: "create", label: "Create", color: "#10b981" },
  { key: "update", label: "Edit",   color: "#f59e0b" },
  { key: "delete", label: "Delete", color: "#ef4444" },
];

// ─── StatCard ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, accent }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 16,
      padding: "20px 24px",
      display: "flex",
      alignItems: "center",
      gap: 16,
      boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
      border: "1px solid #f0f0f0",
      height: "100%",
    }}
  >
    <div
      style={{
        width: 48, height: 48, borderRadius: 12,
        background: accent + "18",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, color: accent, flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 26, fontWeight: 700, color: "#111827", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4, fontWeight: 500 }}>{label}</div>
    </div>
  </div>
);

// ─── ModuleCard ───────────────────────────────────────────────────────────────
const ModuleCard = ({ module, isSuperAdmin, can, navigate }) => {
  const [hovered, setHovered] = useState(false);
  const { accent, bg, label, icon, description, screens } = module;

  // Guard: can may be undefined while permissions are loading
  const accessibleScreens = screens.filter(
    (s) => isSuperAdmin || (can && can(s.permKey, "read"))
  );

  if (accessibleScreens.length === 0) return null;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: bg,
        borderRadius: 16,
        padding: "24px 22px",
        border: hovered ? `1.5px solid ${accent}55` : "1.5px solid transparent",
        boxShadow: hovered ? `0 8px 28px ${accent}22` : "0 1px 3px rgba(0,0,0,0.05)",
        transition: "all 0.2s ease",
        transform: hovered ? "translateY(-2px)" : "none",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      {/* Module header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            width: 44, height: 44, borderRadius: 10,
            background: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, color: accent,
            boxShadow: `0 2px 8px ${accent}25`,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>{label}</div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{description}</div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: `1px solid ${accent}22` }} />

      {/* Screen rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {accessibleScreens.map((screen) => (
          <div
            key={screen.permKey}
            onClick={() => navigate(screen.key)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 8,
              background: "rgba(255,255,255,0.7)",
              cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#fff")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.7)")}
          >
            {/* Screen name */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14, color: accent }}>{screen.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                {screen.label}
              </span>
            </div>

            {/* CRUD permission tags */}
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {ACTIONS.map((action) => {
                const allowed = isSuperAdmin || (can && can(screen.permKey, action.key));
                if (!allowed) return null;
                return (
                  <span
                    key={action.key}
                    style={{
                      fontSize: 10, fontWeight: 600,
                      color: action.color,
                      background: action.color + "15",
                      border: `1px solid ${action.color}30`,
                      borderRadius: 4,
                      padding: "1px 6px",
                    }}
                  >
                    {action.label}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Dashboard ────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const { can, loading: permLoading } = usePermissions();
  const [userDetails, setUserDetails] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    setUserDetails(getUserDetails());
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const isSuperAdmin =
    userDetails?.role === "ROLE_SUPER_ADMIN" ||
    userDetails?.roles === "ROLE_SUPER_ADMIN";

  // Full module list — same source as sidebar, permissions filtered inside ModuleCard
  const modules = getModulesFromMenu({ can, userDetails });

  // Exclude the configuration module from the "core" count on the stats row
  const coreModules = modules.filter((m) => m.key !== "configuration");

  const accessibleScreenCount = coreModules
    .flatMap((m) => m.screens)
    .filter((s) => isSuperAdmin || (can && can(s.permKey, "read"))).length;

  const totalScreenCount = coreModules.flatMap((m) => m.screens).length;

  const rawRole    = userDetails?.roles || userDetails?.role || "";
  const displayRole = formatRole(rawRole);
  const displayName = userDetails?.name || "User";
  const greeting    = getGreeting();

  const dateStr = currentTime.toLocaleDateString("en-IN", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div
      style={{
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        minHeight: "100%",
        background: "#f8fafc",
        padding: "0 0 40px 0",
      }}
    >
      {/* ── Welcome Banner ──────────────────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e3a5f 0%, #0f2746 60%, #1a3a6b 100%)",
          borderRadius: 20,
          padding: "36px 40px",
          marginBottom: 28,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(15,39,70,0.25)",
        }}
      >
        {/* Decorative circles */}
        <div style={{ position: "absolute", top: -40,  right: -40, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -60, right: 80,  width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.03)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: 20,  right: 200, width: 80,  height: 80,  borderRadius: "50%", background: "rgba(6,182,212,0.12)",     pointerEvents: "none" }} />

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16, position: "relative", zIndex: 1 }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(6,182,212,0.15)", border: "1px solid rgba(6,182,212,0.3)", borderRadius: 20, padding: "4px 14px", marginBottom: 16 }}>
              <FcComboChart style={{ fontSize: 14 }} />
              <span style={{ fontSize: 12, color: "#67e8f9", fontWeight: 600, letterSpacing: 0.5 }}>
                STEP TO INNOVATION · IoT EDGE PLATFORM
              </span>
            </div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
              {greeting},{" "}
              <span style={{ color: "#67e8f9" }}>{displayName}</span> 👋
            </h1>
            <p style={{ margin: "10px 0 0", color: "#94a3b8", fontSize: 14 }}>{dateStr}</p>
          </div>

          <div style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 14, padding: "16px 24px", textAlign: "center", minWidth: 140 }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg,#06b6d4,#3b82f6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, margin: "0 auto 10px", boxShadow: "0 4px 12px rgba(6,182,212,0.4)" }}>
              <MdEngineering style={{ color: "#fff" }} />
            </div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{displayName}</div>
            <div style={{ marginTop: 6, display: "inline-block", background: "rgba(6,182,212,0.2)", border: "1px solid rgba(6,182,212,0.4)", borderRadius: 10, padding: "2px 10px", fontSize: 11, color: "#67e8f9", fontWeight: 600 }}>
              {displayRole || "Operator"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats Row ───────────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
        <Col xs={12} sm={12} md={8}>
          <StatCard
            icon={<FcComboChart />}
            label="Modules Accessible"
            value={`${coreModules.length} / ${coreModules.length}`}
            accent="#3b82f6"
          />
        </Col>
        <Col xs={12} sm={12} md={8}>
          <StatCard
            icon={<IoSettingsOutline />}
            label="Screens Accessible"
            value={permLoading ? "…" : `${accessibleScreenCount} / ${totalScreenCount}`}
            accent="#8b5cf6"
          />
        </Col>
        <Col xs={24} sm={24} md={8}>
          <StatCard
            icon={<SiZigbee2Mqtt />}
            label="Role"
            value={displayRole || "Operator"}
            accent="#06b6d4"
          />
        </Col>
      </Row>

      {/* ── Module Cards ────────────────────────────────────────────────── */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Your Access</h2>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#9ca3af" }}>
              Modules and screens available to your role, with permitted actions
            </p>
          </div>
          <Badge count={accessibleScreenCount} style={{ background: "#8b5cf6" }} showZero />
        </div>

        {coreModules.length === 0 ? (
          <div style={{ background: "#fff", borderRadius: 16, padding: "48px 24px", textAlign: "center", border: "1px dashed #e5e7eb" }}>
            <IoSettingsOutline style={{ fontSize: 40, color: "#d1d5db", marginBottom: 12 }} />
            <p style={{ color: "#9ca3af", margin: 0, fontSize: 14 }}>
              No modules assigned yet. Contact your administrator to set up role permissions.
            </p>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {coreModules.map((module) => (
              <Col key={module.key} xs={24} sm={24} md={8}>
                <ModuleCard
                  module={module}
                  isSuperAdmin={isSuperAdmin}
                  can={can}
                  navigate={navigate}
                />
              </Col>
            ))}
          </Row>
        )}
      </div>

      {/* ── Permission legend ────────────────────────────────────────────── */}
      <div style={{ marginTop: 24, padding: "14px 20px", background: "#fff", borderRadius: 12, border: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Permission key:</span>
        {ACTIONS.map((a) => (
          <span key={a.key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
            <span style={{ background: a.color + "15", border: `1px solid ${a.color}30`, borderRadius: 4, padding: "1px 6px", fontWeight: 600, color: a.color }}>
              {a.label}
            </span>
          </span>
        ))}
        <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>
          Only permitted actions are shown per screen
        </span>
      </div>
    </div>
  );
};

export default Dashboard;